import { forwardRef } from 'react';
import { Button, mergeClasses } from '@fluentui/react-components';
import type { ButtonProps as FluentButtonProps } from '@fluentui/react-components';
import { useBasicButtonStyles } from './BasicButton.styles';

/** Allowed button variants */
export type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'subtle' | 'transparent';

/** Maps variant convenience prop to Fluent UI appearance value. */
const variantToAppearance: Record<ButtonVariant, FluentButtonProps['appearance']> = {
  primary: 'primary',
  secondary: 'secondary',
  outline: 'outline',
  subtle: 'subtle',
  transparent: 'transparent',
};

/**
 * Props for BasicButton component.
 * Extends native Fluent UI Button props.
 */
export interface BasicButtonProps extends Omit<FluentButtonProps, 'appearance'> {
  /** Visual variant — maps to Fluent UI appearance */
  variant?: ButtonVariant;
  /**
   * Opt-in to the Figma-aligned 2026 visual style (navy primary / outlined
   * secondary, 2 px radius). Defaults to `false` so existing Planner/Admin
   * usages render the legacy Fluent appearance unchanged.
   */
  figmaVariant?: boolean;
  /**
   * When `true`, the button stretches to `width: 100%`. Used by stacked action
   * bars at narrow viewports (spec 109 US3). Defaults to `false`.
   */
  fullWidth?: boolean;
  /** Render as anchor when used as a link. */
  as?: 'a' | 'button';
  href?: string;
  target?: string;
  rel?: string;
  download?: string | boolean;
}

/**
 * Themed BasicButton — thin wrapper around Fluent UI Button.
 * Applies GEM defaults: variant="secondary", size="medium".
 */
export const BasicButton = forwardRef<HTMLButtonElement, BasicButtonProps>(
  (
    {
      variant = 'secondary',
      size = 'medium',
      figmaVariant = false,
      fullWidth = false,
      className,
      ...rest
    },
    ref
  ) => {
    const styles = useBasicButtonStyles();

    const figmaClass = figmaVariant
      ? variant === 'primary'
        ? styles.figmaPrimary
        : variant === 'secondary'
          ? styles.figmaSecondary
          : undefined
      : undefined;

    return (
      <Button
        ref={ref as React.Ref<HTMLButtonElement & HTMLAnchorElement>}
        appearance={variantToAppearance[variant]}
        size={size}
        className={mergeClasses(styles.root, figmaClass, fullWidth && styles.fullWidth, className)}
        {...(rest as FluentButtonProps)}
      />
    );
  }
);

BasicButton.displayName = 'BasicButton';
